import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, CalendarOff, CalendarRange, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const fld = "rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white";

export default function ScheduleExtrasEditor({ locations = [] }) {
  const { data, refetch, isLoading } = useQuery({
    queryKey: ["enh-schedule-extras"],
    queryFn: () => api.get("/enhancer/me/schedule-extras").then((r) => r.data),
  });
  const [timeOff, setTimeOff] = useState(null);
  const [seasonal, setSeasonal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => {
    if (data) { setTimeOff(data.time_off || []); setSeasonal(data.seasonal || []); }
  }, [data]);

  const to = timeOff || [];
  const sn = seasonal || [];

  const addTimeOff = () => setTimeOff([...to, { start_date: "", end_date: "", all_day: true, start_time: "09:00", end_time: "17:00", note: "" }]);
  const updTimeOff = (i, k, v) => setTimeOff(to.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rmTimeOff = (i) => setTimeOff(to.filter((_, idx) => idx !== i));

  // ---- Tap-to-block month calendar (all-day single-date blocks) ----
  const pad2 = (n) => String(n).padStart(2, "0");
  const todayIso = (() => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; })();
  const isoOf = (day) => `${calMonth.getFullYear()}-${pad2(calMonth.getMonth() + 1)}-${pad2(day)}`;
  const isBlocked = (iso) => to.some((r) => r.all_day !== false && r.start_date && r.start_date <= iso && iso <= (r.end_date || r.start_date));
  const isSingle = (iso) => to.some((r) => r.all_day !== false && r.start_date === iso && (r.end_date || r.start_date) === iso);
  const toggleDate = (iso) => {
    if (iso < todayIso) return;
    if (isSingle(iso)) {
      setTimeOff(to.filter((r) => !(r.all_day !== false && r.start_date === iso && (r.end_date || r.start_date) === iso)));
    } else if (isBlocked(iso)) {
      toast.message("That day is part of a date range below — edit it there.");
    } else {
      setTimeOff([...to, { start_date: iso, end_date: iso, all_day: true, start_time: "09:00", end_time: "17:00", note: "" }]);
    }
  };
  const shiftMonth = (delta) => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + delta, 1));
  const daysInMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();
  const firstDow = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1).getDay();
  const monthLabel = calMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const addSeasonal = () => setSeasonal([...sn, { label: "", start_date: "", end_date: "", days: [], start: "09:00", end: "17:00", location_id: "" }]);
  const updSeasonal = (i, k, v) => setSeasonal(sn.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rmSeasonal = (i) => setSeasonal(sn.filter((_, idx) => idx !== i));
  const toggleDay = (i, d) => {
    const days = sn[i].days || [];
    updSeasonal(i, "days", days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/enhancer/schedule-extras", { time_off: to, seasonal: sn });
      toast.success("Schedule saved");
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (isLoading) return <p className="text-sm text-[#4A5D6B] mt-8"><Loader2 className="animate-spin inline" size={14} /> Loading…</p>;

  return (
    <div className="mt-10 max-w-2xl" data-testid="enh-schedule-extras">
      {/* Time off */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><CalendarOff size={18} className="text-[#B5794E]" /> Time off</h3>
        <button onClick={addTimeOff} data-testid="enh-add-timeoff" className="eyw-btn-ghost !py-1.5 text-xs"><Plus size={14} /> Add time off</button>
      </div>
      <p className="text-xs text-[#4A5D6B] mb-3">Close specific dates. Time off overrides your weekly and seasonal availability — members can't book you then.</p>

      {/* Tap-to-block month calendar */}
      <div className="eyw-card p-4 mb-4" data-testid="timeoff-calendar">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={() => shiftMonth(-1)} data-testid="timeoff-cal-prev" className="p-1.5 rounded-full hover:bg-[#F1EEE8] text-[#4A5D6B]"><ChevronLeft size={18} /></button>
          <div className="font-medium text-[#1A242B]" data-testid="timeoff-cal-label">{monthLabel}</div>
          <button type="button" onClick={() => shiftMonth(1)} data-testid="timeoff-cal-next" className="p-1.5 rounded-full hover:bg-[#F1EEE8] text-[#4A5D6B]"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {DAYS.map((d) => <div key={d} className="text-[10px] uppercase tracking-wide text-[#8FA89B] py-1">{d}</div>)}
          {Array.from({ length: firstDow }).map((_, i) => <div key={`b${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const iso = isoOf(day);
            const blocked = isBlocked(iso);
            const past = iso < todayIso;
            return (
              <button
                key={day}
                type="button"
                disabled={past}
                onClick={() => toggleDate(iso)}
                data-testid={`timeoff-cal-day-${iso}`}
                aria-pressed={blocked}
                className={`h-9 rounded-lg text-sm transition-colors ${past ? "text-[#CDCDC5] cursor-not-allowed"
                  : blocked ? "bg-[#B5794E] text-white font-medium"
                    : "text-[#2A3942] hover:bg-[#F1EEE8]"}`}>
                {day}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-[#8FA89B] mt-3">Tap a day to block it (marked <span className="inline-block align-middle h-3 w-3 rounded bg-[#B5794E]" />). Tap again to unblock. Remember to <b>Save</b> below. For multi-day or partial-day blocks, add a range underneath.</p>
      </div>

      <div className="space-y-3">
        {to.length === 0 && <p className="text-sm text-[#8FA89B]">No time off scheduled.</p>}
        {to.map((r, i) => (
          <div key={i} data-testid={`timeoff-row-${i}`} className="eyw-card p-4 space-y-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" value={r.start_date || ""} onChange={(e) => updTimeOff(i, "start_date", e.target.value)} data-testid={`timeoff-start-${i}`} className={fld} />
              <span className="text-[#4A5D6B] text-sm">to</span>
              <input type="date" value={r.end_date || ""} onChange={(e) => updTimeOff(i, "end_date", e.target.value)} data-testid={`timeoff-end-${i}`} className={fld} />
              <button onClick={() => rmTimeOff(i)} className="text-red-500 ml-auto" data-testid={`timeoff-remove-${i}`}><Trash2 size={16} /></button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 text-sm text-[#2A3942] cursor-pointer">
                <input type="checkbox" checked={r.all_day !== false} onChange={(e) => updTimeOff(i, "all_day", e.target.checked)} className="accent-[#144F75]" data-testid={`timeoff-allday-${i}`} /> All day
              </label>
              {r.all_day === false && (
                <>
                  <input type="time" value={r.start_time || "09:00"} onChange={(e) => updTimeOff(i, "start_time", e.target.value)} className={fld} />
                  <span className="text-[#4A5D6B] text-sm">–</span>
                  <input type="time" value={r.end_time || "17:00"} onChange={(e) => updTimeOff(i, "end_time", e.target.value)} className={fld} />
                </>
              )}
              <input type="text" placeholder="Note (optional)" value={r.note || ""} onChange={(e) => updTimeOff(i, "note", e.target.value)} className={`${fld} flex-1 min-w-[120px]`} />
            </div>
          </div>
        ))}
      </div>

      {/* Seasonal */}
      <div className="flex items-center justify-between mb-3 mt-8">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><CalendarRange size={18} className="text-[#144F75]" /> Seasonal hours</h3>
        <button onClick={addSeasonal} data-testid="enh-add-seasonal" className="eyw-btn-ghost !py-1.5 text-xs"><Plus size={14} /> Add season</button>
      </div>
      <p className="text-xs text-[#4A5D6B] mb-3">Availability that applies only during a date range (e.g. summer hours, retreat season).</p>
      <div className="space-y-3">
        {sn.length === 0 && <p className="text-sm text-[#8FA89B]">No seasonal schedules.</p>}
        {sn.map((r, i) => (
          <div key={i} data-testid={`seasonal-row-${i}`} className="eyw-card p-4 space-y-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <input type="text" placeholder="Label (e.g. Summer hours)" value={r.label || ""} onChange={(e) => updSeasonal(i, "label", e.target.value)} data-testid={`seasonal-label-${i}`} className={`${fld} flex-1 min-w-[140px]`} />
              <button onClick={() => rmSeasonal(i)} className="text-red-500" data-testid={`seasonal-remove-${i}`}><Trash2 size={16} /></button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" value={r.start_date || ""} onChange={(e) => updSeasonal(i, "start_date", e.target.value)} data-testid={`seasonal-start-${i}`} className={fld} />
              <span className="text-[#4A5D6B] text-sm">to</span>
              <input type="date" value={r.end_date || ""} onChange={(e) => updSeasonal(i, "end_date", e.target.value)} data-testid={`seasonal-end-${i}`} className={fld} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d, idx) => (
                <button key={idx} type="button" onClick={() => toggleDay(i, idx)} className={`h-8 w-8 rounded-full text-xs font-medium ${(r.days || []).includes(idx) ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B]"}`}>{d}</button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input type="time" value={r.start || "09:00"} onChange={(e) => updSeasonal(i, "start", e.target.value)} className={fld} />
              <span className="text-[#4A5D6B] text-sm">–</span>
              <input type="time" value={r.end || "17:00"} onChange={(e) => updSeasonal(i, "end", e.target.value)} className={fld} />
              <select value={r.location_id || ""} onChange={(e) => updSeasonal(i, "location_id", e.target.value)} className={fld}>
                <option value="">Any location</option>
                <option value="virtual">Virtual / Online</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid="enh-save-schedule-extras" className="eyw-btn !py-2 text-sm mt-6 disabled:opacity-60">
        {saving && <Loader2 className="animate-spin" size={16} />} Save time off & seasonal hours
      </button>
    </div>
  );
}
